import { FormArray, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnChanges, signal, ViewEncapsulation } from "@angular/core";
import { CdkDragDrop, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { BmExpGroupFg, BudgetModelFormGroup } from "../pojo/bm-form";
import {
  TableBudgetModelBodyMgmComponent,
} from "./sections/table-budget-model-body-mgm/table-budget-model-body-mgm.component";
import {
  TableBudgetModelHeadersComponent,
} from "./sections/table-budget-model-headers/table-budget-model-headers.component";
import {
  TableBudgetModelBodyOperatingExpensesComponent,
} from "./sections/table-budget-model-body-operating-expenses/table-budget-model-body-operating-expenses.component";
import {
  TableBudgetModelBodyOrgExpensesComponent,
} from "./sections/table-budget-model-body-org-expenses/table-budget-model-body-org-expenses.component";
import {
  TableBudgetModelBodyTotalExpensesComponent,
} from "./sections/table-budget-model-body-total-expenses/table-budget-model-body-total-expenses.component";
import { TableBudgetModelTfootComponent } from "./sections/table-budget-model-tfoot/table-budget-model-tfoot.component";
import { BmPeriodEnum } from "../pojo/bm-classes";
import { startWith } from "rxjs/operators";
import { tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BmDataFillingType, BmGroupType } from "../pojo/bm-enums";
import { values } from "lodash";
import { BmLoadingTableComponent } from "../loading-state/bm-loading-table/bm-loading-table.component";

@Component({
  selector: 'app-table-budget-model',
  templateUrl: './table-budget-model.component.html',
  styleUrls: ['./table-budget-model.component.scss'],
  imports: [
    ReactiveFormsModule,
    CdkDropList,
    TableBudgetModelHeadersComponent,
    TableBudgetModelBodyMgmComponent,
    TableBudgetModelBodyOperatingExpensesComponent,
    TableBudgetModelBodyOrgExpensesComponent,
    TableBudgetModelBodyTotalExpensesComponent,
    TableBudgetModelTfootComponent,
    BmLoadingTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TableBudgetModelComponent implements OnChanges {
  protected readonly BmPeriodEnum = BmPeriodEnum;
  protected readonly BmDataFillingType = BmDataFillingType;
  protected readonly BmGroupType = BmGroupType;
  protected readonly values = values;

  @Input() budgetForm: BudgetModelFormGroup;

  loadingTable$$ = signal(false);
  collapsedYears$$ = signal(new Map<number, boolean>());
  isEditState$$ = signal(false);

  private destroyRef = inject(DestroyRef);

  ngOnChanges(): void {
    this.subscribeOnCollapsedYears();
    this.subscribeOnEditState();
  }

  subscribeOnCollapsedYears(): void {
    if (!this.budgetForm) {
      return;
    }
    this.budgetForm.controls.collapsedYears.valueChanges.pipe(
      startWith(this.budgetForm.value.collapsedYears),
      tap((years) => {
        const map = new Map<number, boolean>();
        years.forEach((year) => map.set(year, true));
        this.collapsedYears$$.set(map);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  subscribeOnEditState(): void {
    if (!this.budgetForm) {
      return;
    }
    this.budgetForm.controls.isEditState.valueChanges.pipe(
      startWith(false),
      tap((isEditState) => {
        this.loadingTable$$.set(true);
        this.isEditState$$.set(isEditState);
        setTimeout(() => {
          this.loadingTable$$.set(false);
        }, 400); // set minimal loading time
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /**
   * DRAG DROP - OPERATING EXPENSES or ORGANIZATIONAL EXPENSES based on groupsFA
   * Handle drag and drop reordering of expenses groups
   */
  changeOrderInExpensesGroup(event: CdkDragDrop<string[]>, groupsFA: FormArray<FormGroup<BmExpGroupFg>>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // Move item in form array
    moveItemInArray(groupsFA.controls, event.previousIndex, event.currentIndex);

    // Update priority for each group after reorder
    groupsFA.controls.forEach((group, index) => {
      group.patchValue({ priority: index });
    });
  }
}
